// DS2.5
import React, { useContext } from 'react';
import classnames from 'classnames';
import PlainButtonOrLink, {
  Props as PlainButtonOrLinkProps,
} from '../PlainButtonOrLink';
import { Props as IconProps, IconSize } from '../../Icons/IconWrap';
import { ButtonSize } from '../types';
import SimpleTooltip from '../../tooltips/dsPreset/SimpleTooltip';
import { OffsetModifier } from '../../popper/types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import type { RequiresAccessibleLabel } from '@wix/da-a11y/pkg/typeHelpers';

import s from './IconButton.scss';

interface BaseProps extends Omit<PlainButtonOrLinkProps, 'children'> {
  size: ButtonSize;
  iconSizeOverride?: IconSize;
  iconClassName?: string;
  icon: React.ComponentType<IconProps>;
  tooltipText?: string | false;
  tooltipOffset?: OffsetModifier;
  tooltipHoverDelay?: number;
  variant?:
    | 'main'
    | 'approval-green'
    | 'destructive-red'
    | 'edit-purple'
    | 'core-orange'
    | 'originals-ring';
  iconStyle?: React.CSSProperties;
  noPaddings?: boolean;
}

export type Props = RequiresAccessibleLabel<BaseProps>;

const IconButton: React.FC<Props> = ({
  size,
  iconSizeOverride,
  iconClassName,
  className,
  icon: IconComponent,
  variant = 'main',
  tooltipText,
  tooltipOffset,
  tooltipHoverDelay,
  iconStyle,
  noPaddings,
  ...props
}) => {
  const { device } = useContext(SessionContext);
  const iconSizes = {
    small: IconSize.SMALLER,
    medium: IconSize.MEDIUM,
    large: IconSize.MEDIUM,
  };

  const icon = (
    <IconComponent
      className={classnames(s['icon'], iconClassName)}
      size={iconSizeOverride || iconSizes[size]}
      disableDefaultColors
      style={iconStyle}
    />
  );
  return (
    <PlainButtonOrLink
      className={classnames(
        s['root'],
        s[`size-${size}`],
        variant && s[`variant-${variant}`],
        noPaddings && s['no-paddings'],
        props.disabled && s['disabled'],
        props.loading && s['loading'],
        className
      )}
      {...props}
    >
      {!device?.isMobileDevice && tooltipText ? (
        <SimpleTooltip
          usePortal
          text={tooltipText}
          offsetModifier={tooltipOffset}
          hoverDelay={tooltipHoverDelay}
          listenOnClicks={false}
        >
          {icon}
        </SimpleTooltip>
      ) : (
        icon
      )}
    </PlainButtonOrLink>
  );
};
IconButton.displayName = 'IconButton';

export default IconButton;
